import { useEffect, useMemo, useState } from "react";
import { sortRunners } from "../data/analytics";
import { formatDateTime, formatDuration, formatNumber } from "../data/formatters";
import type { NormalizedRunner, SortKey, SortRule } from "../types";
import { Panel } from "../components/Panel";

const PAGE_SIZE = 50;
const COLUMN_LABELS: Record<SortKey, string> = {
  achievementOrder: "達標次序",
  nickname: "暱稱",
  age: "年齡",
  gender: "性別",
  location: "地點",
  achievedAt: "達標時間",
  totalTime: "累積活動總時間",
  activityCount: "跑步次數",
};

export function RunnersPage({ runners }: { runners: NormalizedRunner[] }) {
  const [showUserId, setShowUserId] = useState(false);
  const [page, setPage] = useState(1);
  const [sortRules, setSortRules] = useState<SortRule[]>([
    { key: "achievementOrder", direction: "asc" },
  ]);
  const sorted = useMemo(() => sortRunners(runners, sortRules), [runners, sortRules]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const visible = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [runners]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const updateSort = (key: SortKey, keepExisting: boolean) => {
    const existing = sortRules.find((rule) => rule.key === key);
    const next: SortRule = { key, direction: existing?.direction === "asc" ? "desc" : "asc" };
    setSortRules(keepExisting
      ? [...sortRules.filter((rule) => rule.key !== key), next]
      : [next]);
  };

  return (
    <div className="page-stack">
      <Panel
        title="跑者資料明細"
        eyebrow="RUNNER RECORDS"
        aside={<span className="sample">{formatNumber(runners.length)} 筆符合結果</span>}
      >
        <div className="table-toolbar">
          <div>
            <button className={`button button--secondary${showUserId ? " is-active" : ""}`} type="button" onClick={() => setShowUserId((value) => !value)}>
              {showUserId ? "隱藏" : "顯示"}使用者 ID
            </button>
            <span className="text-muted">點欄名排序；Shift + 點擊可加入多欄排序。</span>
          </div>
          <button className="button button--ghost" type="button" onClick={() => setSortRules([{ key: "achievementOrder", direction: "asc" }])}>重設排序</button>
        </div>

        <div className="sort-rules" aria-label="目前排序條件">
          {sortRules.map((rule, index) => (
            <span key={rule.key}>{index + 1}. {COLUMN_LABELS[rule.key]} {rule.direction === "asc" ? "↑" : "↓"}</span>
          ))}
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortableHead column="achievementOrder" rules={sortRules} onSort={updateSort} />
                {showUserId && <th>使用者 ID</th>}
                <SortableHead column="nickname" rules={sortRules} onSort={updateSort} />
                <SortableHead column="age" rules={sortRules} onSort={updateSort} />
                <th>年齡層</th>
                <SortableHead column="gender" rules={sortRules} onSort={updateSort} />
                <SortableHead column="location" rules={sortRules} onSort={updateSort} />
                <SortableHead column="achievedAt" rules={sortRules} onSort={updateSort} />
                <SortableHead column="totalTime" rules={sortRules} onSort={updateSort} />
                <SortableHead column="activityCount" rules={sortRules} onSort={updateSort} />
              </tr>
            </thead>
            <tbody>
              {visible.map((runner) => (
                <tr key={runner.recordKey}>
                  <td><span className="mono muted">#{runner.achievementOrder}</span></td>
                  {showUserId && <td><span className="mono">{runner.userId === -1 ? "非公開 (-1)" : runner.userId}</span></td>}
                  <td><strong className="runner-name">{runner.nickname}</strong></td>
                  <td>{runner.age ?? "—"}</td>
                  <td>{runner.ageGroup}</td>
                  <td><span className={`status-dot status-dot--${runner.gender}`} />{runner.genderLabel}</td>
                  <td>{runner.locationLabel}</td>
                  <td className="nowrap">{formatDateTime(runner.achievedAt)}</td>
                  <td className="mono nowrap">{formatDuration(runner.totalTimeSeconds)}</td>
                  <td>{runner.activityCount.toLocaleString()}</td>
                </tr>
              ))}
              {!visible.length && (
                <tr><td colSpan={showUserId ? 10 : 9}><div className="empty-state empty-state--small"><span>×</span><p>沒有符合目前篩選條件的跑者。</p></div></td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span>第 {page.toLocaleString()} / {pageCount.toLocaleString()} 頁</span>
          <div>
            <button className="button button--secondary" type="button" disabled={page === 1} onClick={() => setPage(1)}>最前</button>
            <button className="button button--secondary" type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>上一頁</button>
            <button className="button button--secondary" type="button" disabled={page === pageCount} onClick={() => setPage((value) => value + 1)}>下一頁</button>
            <button className="button button--secondary" type="button" disabled={page === pageCount} onClick={() => setPage(pageCount)}>最後</button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function SortableHead({
  column,
  rules,
  onSort,
}: {
  column: SortKey;
  rules: SortRule[];
  onSort: (key: SortKey, keepExisting: boolean) => void;
}) {
  const index = rules.findIndex((rule) => rule.key === column);
  const rule = rules[index];
  return (
    <th>
      <button type="button" onClick={(event) => onSort(column, event.shiftKey)}>
        {COLUMN_LABELS[column]}
        {rule && <span>{rule.direction === "asc" ? "↑" : "↓"}{rules.length > 1 ? index + 1 : ""}</span>}
      </button>
    </th>
  );
}
